import React, { useEffect, useRef, useState, useMemo } from 'react';
import cn from "../../../../utils/cn";
import PropTypes from 'prop-types';
import Icon from "../../../Display/Icon/Icon";
import Popover from "../../../Display/Popover/Popover";
import HoursOfOperation from "../../../Display/HoursOfOperation/HoursOfOperation";
import { SEARCH_ROLES, SCROLL_TO } from "../utils/constants";
import { flatServicesPropType, hoursPropType } from "../utils/propTypes";
import filterServiceTypes from "../utils/filterServiceTypes";
import unflattenServiceTypes from "../utils/unflattenServiceTypes";
import ReferralStatus from "./ReferralStatus";
import SupersetAddResource from "../SupersetAddResource/SupersetAddResource";
import StopPropagation from "./StopPropagation";
import OffersSpecializedScreeningLabel from "./OffersSpecializedScreeningLabel";
import RequiresSpecializedScreeningLabel from "./RequiresSpecializedScreeningLabel";
import "./ProgramCard.scss";
import TextButton from "../../../Display/TextButton/TextButton";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DISPLAY_NAME = 'search-program-card';
function Heading(_ref) {
  let {
    title,
    headingLevel,
    className
  } = _ref;
  const validHeadingLevels = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
  const safeHeading = headingLevel ? headingLevel.toLowerCase() : '';
  const Title = validHeadingLevels.includes(safeHeading) ? safeHeading : 'p';
  return /*#__PURE__*/_jsx(Title, {
    className: className,
    children: title
  });
}
Heading.propTypes = {
  title: PropTypes.string.isRequired,
  headingLevel: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  className: PropTypes.string
};
Heading.defaultProps = {
  headingLevel: 'p',
  className: ''
};
function ProgramCard(_ref2) {
  let {
    selectedServicesIds,
    className,
    description,
    deliveryLocation,
    distance,
    feeSchedule,
    hasHeaderDivider,
    headingLevel,
    hours,
    id,
    isActive,
    isPrioritized,
    isSelected,
    isServingArea,
    onSelectProgram,
    onCardClick,
    onCardHover,
    phone,
    provider,
    providerLicensed,
    receivingReferrals,
    serviceNames,
    services,
    status,
    title,
    isProgramDetail,
    uniqueKey,
    isSuperset,
    appRoles,
    favoritesSectionRenderFn,
    offersSpecializedScreening,
    showRequiresFeeScheduleScreening,
    forwardReferralParams,
    // FLAGS,
    uup459SupersetPhase2,
    cerb1367ResourceListDetailEnhancements,
    cerbNy1115
  } = _ref2;
  const ROW_CLASSES = cn("".concat(DISPLAY_NAME, "__row"), 'flex', isProgramDetail ? 'mb-0 justify-between w-full flex-wrap' : 'mb-2');
  const ROW_DIV_CLASSES = cn('pr-4 flex flex-initial box-border flex-wrap', isProgramDetail && 'mb-0 w-full');
  const stOverflowBtnId = "st-popover-button-".concat(uniqueKey);
  const descOverflowBtnId = "desc-popover-button-".concat(uniqueKey);
  const serviceTypesRef = useRef();
  const descriptionRef = useRef();
  const [areStsOverflowing, setAreStsOverflowing] = useState(false);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);

  // TODO: window resize + debounce (nice to have)
  useEffect(() => {
    const {
      offsetWidth,
      scrollWidth
    } = serviceTypesRef.current;
    if (scrollWidth > offsetWidth) {
      setAreStsOverflowing(true);
    }
  }, [serviceTypesRef]);
  useEffect(() => {
    const {
      offsetHeight,
      scrollHeight
    } = descriptionRef.current;
    if (scrollHeight > offsetHeight) {
      setIsDescriptionOverflowing(true);
    }
  }, [descriptionRef]);
  const filteredServices = unflattenServiceTypes(filterServiceTypes(services, appRoles.includes(SEARCH_ROLES.PAYMENTS)));
  const iconClasses = 'h-4 w-4 mr-1 fill-current text-grey-500';
  const isSupersetPagination = !isProgramDetail && isSuperset && Boolean(onSelectProgram);
  const popoverWidth = useMemo(() => !isProgramDetail ? "64" : "40", [isProgramDetail]); // 16rem via 'w-64' property or 10rem via 'w-40'

  const togglePopover = () => {
    setIsPopoverVisible(!isPopoverVisible);
  };
  return /*#__PURE__*/_jsxs("article", {
    className: cn(DISPLAY_NAME, 'bg-white border border-solid border-dark-pale-blue text-grey leading-5 font-normal not-italic mb-4', !isProgramDetail && 'hover:bg-light-pale-blue', isActive && "".concat(DISPLAY_NAME, "--active bg-light-pale-blue cursor-pointer"), className),
    children: [/*#__PURE__*/_jsxs("div", {
      className: cn("".concat(DISPLAY_NAME, "__selectionBox"), isProgramDetail && 'cursor-default'),
      role: "button",
      tabIndex: 0,
      onClick: onCardClick,
      onKeyDown: event => event.key === 'Enter' && onCardClick(),
      onMouseEnter: () => onCardHover(id),
      onMouseLeave: () => onCardHover(null),
      onFocus: () => onCardHover(id),
      onBlur: () => onCardHover(null),
      "aria-label": title,
      children: [/*#__PURE__*/_jsxs("header", {
        className: cn("".concat(DISPLAY_NAME, "__header"), 'flex justify-between', 'mb-0', hasHeaderDivider && "".concat(DISPLAY_NAME, "__header--divider border-b border-solid border-dark-pale-blue p-6"), isProgramDetail && 'pt-6 pr-6 pb-0 pl-6'),
        children: [/*#__PURE__*/_jsxs("div", {
          className: "flex flex-auto",
          children: [/*#__PURE__*/_jsxs("div", {
            className: "".concat(DISPLAY_NAME, "__program-title"),
            children: [/*#__PURE__*/_jsx(Heading, {
              title: title,
              headingLevel: headingLevel
            }), !isProgramDetail && /*#__PURE__*/_jsxs("p", {
              children: ["Provided By", ' ', /*#__PURE__*/_jsx("span", {
                className: "".concat(DISPLAY_NAME, "__provider-name color-purple"),
                children: provider
              })]
            })]
          }), distance && distance !== '0.0' && /*#__PURE__*/_jsx("div", {
            className: "".concat(DISPLAY_NAME, "__program-distance whitespace-no-wrap"),
            children: /*#__PURE__*/_jsxs("p", {
              className: "".concat(DISPLAY_NAME, "__info-text"),
              children: [/*#__PURE__*/_jsx("span", {
                className: "font-medium-font",
                children: "Distance: "
              }), distance.replace('mi', 'miles')]
            })
          })]
        }), favoritesSectionRenderFn && favoritesSectionRenderFn()]
      }), /*#__PURE__*/_jsxs("div", {
        className: cn("".concat(DISPLAY_NAME, "__info"), isProgramDetail ? 'pt-1 pr-6 pb-6 pl-6' : 'p-6'),
        children: [/*#__PURE__*/_jsxs("div", {
          className: cn(ROW_CLASSES, {
            'mb-4': showRequiresFeeScheduleScreening || offersSpecializedScreening
          }),
          children: [/*#__PURE__*/_jsxs("div", {
            className: "".concat(DISPLAY_NAME, "__referrals ").concat(ROW_DIV_CLASSES, " flex-col"),
            children: [/*#__PURE__*/_jsx(ReferralStatus, {
              status: status,
              receivingReferrals: receivingReferrals,
              uup459SupersetPhase2: uup459SupersetPhase2,
              providerLicensed: providerLicensed,
              programId: id
            }), cerbNy1115 && /*#__PURE__*/_jsx(RequiresSpecializedScreeningLabel, {
              showRequiresFeeScheduleScreening: showRequiresFeeScheduleScreening,
              className: "mt-4 flex items-center",
              programId: id
            }), cerbNy1115 && /*#__PURE__*/_jsx(OffersSpecializedScreeningLabel, {
              offersSpecializedScreening: offersSpecializedScreening,
              className: "mt-4 flex items-center",
              programId: id
            })]
          }), isServingArea && /*#__PURE__*/_jsxs("div", {
            className: "".concat(DISPLAY_NAME, "__service-area whitespace-no-wrap"),
            children: [/*#__PURE__*/_jsx(Icon, {
              icon: "Radar",
              className: iconClasses
            }), /*#__PURE__*/_jsx("span", {
              className: "font-medium-font",
              children: "Serves This Area"
            })]
          }), showRequiresFeeScheduleScreening && /*#__PURE__*/_jsx("div", {
            className: "".concat(DISPLAY_NAME, "__prioritized ").concat(ROW_DIV_CLASSES, " mt-auto"),
            children: /*#__PURE__*/_jsxs("p", {
              className: "flex items-center",
              children: [/*#__PURE__*/_jsx(Icon, {
                icon: "DollarSign",
                className: iconClasses
              }), /*#__PURE__*/_jsx("span", {
                className: "font-medium-font",
                children: "Funded Program"
              })]
            })
          })]
        }), isPrioritized && /*#__PURE__*/_jsx("div", {
          className: ROW_CLASSES,
          children: /*#__PURE__*/_jsx("div", {
            className: "".concat(DISPLAY_NAME, "__prioritized ").concat(ROW_DIV_CLASSES),
            children: /*#__PURE__*/_jsxs("p", {
              className: "flex items-center",
              children: [/*#__PURE__*/_jsx(Icon, {
                icon: "Medal",
                className: iconClasses
              }), /*#__PURE__*/_jsx("span", {
                className: "font-medium-font",
                children: "Your Organization Recommends"
              })]
            })
          })
        }), feeSchedule && !showRequiresFeeScheduleScreening && /*#__PURE__*/_jsx("div", {
          className: ROW_CLASSES,
          children: /*#__PURE__*/_jsx("div", {
            className: "".concat(DISPLAY_NAME, "__prioritized ").concat(ROW_DIV_CLASSES),
            children: /*#__PURE__*/_jsxs("p", {
              className: "flex items-center",
              children: [/*#__PURE__*/_jsx(Icon, {
                icon: "DollarSign",
                className: iconClasses
              }), /*#__PURE__*/_jsx("span", {
                className: "font-medium-font",
                children: "Funded Program"
              })]
            })
          })
        }), /*#__PURE__*/_jsxs("div", {
          className: ROW_CLASSES,
          children: [/*#__PURE__*/_jsx("div", {
            className: "".concat(DISPLAY_NAME, "__service-types ").concat(ROW_DIV_CLASSES),
            children: /*#__PURE__*/_jsxs("div", {
              className: cn("".concat(DISPLAY_NAME, "__info-text flex items-center max-w-sm"), 'text-text-blue'),
              children: [/*#__PURE__*/_jsxs("span", {
                className: "font-medium-font",
                children: ["Service Types Offered:", ' ']
              }), /*#__PURE__*/_jsxs("span", {
                className: "".concat(DISPLAY_NAME, "__service-types-wrapper flex"),
                children: [/*#__PURE__*/_jsx("span", {
                  className: "".concat(DISPLAY_NAME, "__service-types-list flex-shrink truncate"),
                  ref: serviceTypesRef,
                  children: serviceNames.length > 0 ? serviceNames === null || serviceNames === void 0 ? void 0 : serviceNames.join(', ') : 'None'
                }), areStsOverflowing && (cerb1367ResourceListDetailEnhancements && !isProgramDetail ? /*#__PURE__*/_jsx(StopPropagation, {
                  as: "button",
                  type: "button",
                  id: stOverflowBtnId,
                  className: "".concat(DISPLAY_NAME, "__overflow-button p-0 border-none appearance-none bg-transparent whitespace-no-wrap -ml-1 z-1"),
                  onClick: () => onCardClick(SCROLL_TO.SERVICE_TYPES_SCROLL_POSITION),
                  children: "\xA0(See More)"
                }) : /*#__PURE__*/_jsx(StopPropagation, {
                  as: "button",
                  type: "button",
                  id: stOverflowBtnId,
                  className: "".concat(DISPLAY_NAME, "__overflow-button p-0 border-none appearance-none bg-transparent whitespace-no-wrap -ml-1 z-1"),
                  onClick: e => {
                    e.stopPropagation();
                    togglePopover();
                  },
                  children: /*#__PURE__*/_jsx(TextButton, {
                    className: "".concat(DISPLAY_NAME, "__overflow-button flex self-end justify-self-end p-0 border-none appearance-none bg-transparent whitespace-no-wrap -ml-1"),
                    onClick: () => {},
                    children: "\xA0(See More)"
                  })
                }))]
              })]
            })
          }), phone && /*#__PURE__*/_jsxs("div", {
            className: "".concat(DISPLAY_NAME, "__phone-number whitespace-no-wrap"),
            children: [/*#__PURE__*/_jsx(Icon, {
              icon: "Phone",
              className: iconClasses
            }), phone]
          })]
        }), /*#__PURE__*/_jsx("div", {
          className: ROW_CLASSES,
          children: deliveryLocation.length > 0 && /*#__PURE__*/_jsx("div", {
            className: "".concat(DISPLAY_NAME, "__program-delivery"),
            children: /*#__PURE__*/_jsxs("p", {
              className: "".concat(DISPLAY_NAME, "__info-text flex items-center"),
              children: [/*#__PURE__*/_jsx("span", {
                className: "font-medium-font",
                children: "Program Delivery:"
              }), ' ', deliveryLocation.join(', ')]
            })
          })
        }), /*#__PURE__*/_jsx(StopPropagation, {
          as: "div",
          className: ROW_CLASSES,
          "aria-label": "Hours of Operation",
          children: /*#__PURE__*/_jsx(HoursOfOperation, {
            hours: hours,
            hoursLabel: "Hours:",
            showAll: true,
            showToggle: true,
            color: "#525F73"
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "".concat(DISPLAY_NAME, "__description"),
          children: /*#__PURE__*/_jsxs("div", {
            className: "".concat(DISPLAY_NAME, "__info-text text-text-blue"),
            children: [/*#__PURE__*/_jsx("span", {
              className: "font-medium-font mb-1",
              children: "Program Description:"
            }), /*#__PURE__*/_jsxs("span", {
              className: "".concat(DISPLAY_NAME, "__description-wrapper flex flex-none justify-between leading-5"),
              children: [/*#__PURE__*/_jsx("span", {
                className: "".concat(DISPLAY_NAME, "__description-text overflow-hidden inline leading-5 max-h-60px")
                // eslint-disable-next-line react/no-danger
                ,
                dangerouslySetInnerHTML: {
                  __html: description
                },
                ref: descriptionRef
              }), isDescriptionOverflowing && (cerb1367ResourceListDetailEnhancements && !isProgramDetail ? /*#__PURE__*/_jsx(StopPropagation, {
                as: "button",
                type: "button",
                id: descOverflowBtnId
                // eslint-disable-next-line max-len
                ,
                className: "".concat(DISPLAY_NAME, "__overflow-button flex flex-shrink-0 self-end justify-self-end p-0 border-none appearance-none bg-transparent whitespace-no-wrap -ml-1"),
                onClick: () => onCardClick(SCROLL_TO.DESCRIPTION_SCROLL_POSITION),
                children: "... (See More)"
              }) : /*#__PURE__*/_jsx(Popover, {
                id: "".concat(descOverflowBtnId, "__popover"),
                className: "search-app-descOverflow-popover",
                childrenClassName: "flex",
                content: /*#__PURE__*/_jsx("div", {
                  dangerouslySetInnerHTML: {
                    __html: description
                  }
                }),
                position: "top",
                width: popoverWidth,
                showArrow: true,
                trigger: "click",
                onClick: e => {
                  e.stopPropagation();
                },
                children: /*#__PURE__*/_jsx(TextButton, {
                  className: "".concat(DISPLAY_NAME, "__overflow-button flex self-end justify-self-end p-0 border-none appearance-none bg-transparent whitespace-no-wrap -ml-1"),
                  onClick: () => {},
                  children: "... (See More)"
                })
              }))]
            })]
          })
        }), isSupersetPagination && serviceNames.length > 0 && /*#__PURE__*/_jsx(StopPropagation, {
          as: "div",
          children: /*#__PURE__*/_jsx(SupersetAddResource, {
            selectedServicesIds: selectedServicesIds,
            services: filteredServices,
            programId: id,
            isSelected: isSelected,
            onSelectProgram: onSelectProgram,
            forwardReferralParams: forwardReferralParams
          })
        }), isSupersetPagination && serviceNames.length === 0 && /*#__PURE__*/_jsx("footer", {
          className: "flex border-t border-solid border-dark-pale-blue mt-2 pt-2",
          children: /*#__PURE__*/_jsxs("div", {
            className: "pl-3",
            children: [/*#__PURE__*/_jsx(Icon, {
              icon: "ExclamationCircle",
              className: "mr-1",
              color: "#182A44"
            }), /*#__PURE__*/_jsx("span", {
              children: "This resource can't be added because it doesn't have a service type."
            })]
          })
        }), !isProgramDetail && !isSuperset && Boolean(onSelectProgram) && /*#__PURE__*/_jsx("footer", {
          className: "flex justify-center p-6",
          children: /*#__PURE__*/_jsxs(StopPropagation, {
            as: "button",
            type: "button",
            className: cn("".concat(DISPLAY_NAME, "__button"), "".concat(DISPLAY_NAME, "__button-").concat(isSelected ? 'remove' : 'add'), 'border border-solid border-dark-blue p-4 rounded', 'flex items-center justify-center', isSelected ? 'bg-white text-dark-blue' : 'bg-dark-blue text-white'),
            onClick: () => onSelectProgram(),
            children: [/*#__PURE__*/_jsx(Icon, {
              icon: isSelected ? 'MinusCircle' : 'Plus',
              className: cn("".concat(DISPLAY_NAME, "__button-icon"), "".concat(DISPLAY_NAME, "__button-icon-").concat(isSelected ? 'remove' : 'add'), 'mr-4'),
              color: isSelected ? '#182A44' : '#FFFFFF'
            }), isSelected ? 'Remove Program' : 'Add Program']
          })
        })]
      })]
    }), !cerb1367ResourceListDetailEnhancements && areStsOverflowing && /*#__PURE__*/_jsx(Popover, {
      id: "".concat(stOverflowBtnId, "__popover"),
      className: "search-app-stOverflow-popover",
      content: serviceNames.join(', '),
      position: "top",
      width: popoverWidth,
      showArrow: false,
      trigger: "manual",
      isVisible: isPopoverVisible,
      onClick: e => {
        e.stopPropagation();
      }
    })]
  });
}
ProgramCard.propTypes = {
  favoritesSectionRenderFn: PropTypes.func,
  selectedServicesIds: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  headingLevel: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  hasHeaderDivider: PropTypes.bool,
  description: PropTypes.string.isRequired,
  provider: PropTypes.string.isRequired,
  providerLicensed: PropTypes.bool.isRequired,
  distance: PropTypes.string,
  receivingReferrals: PropTypes.bool.isRequired,
  isPrioritized: PropTypes.bool,
  serviceNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  services: flatServicesPropType.isRequired,
  status: PropTypes.string,
  deliveryLocation: PropTypes.arrayOf(PropTypes.string),
  isServingArea: PropTypes.bool,
  phone: PropTypes.string,
  hours: hoursPropType,
  onSelectProgram: PropTypes.func,
  onCardClick: PropTypes.func,
  onCardHover: PropTypes.func,
  isActive: PropTypes.bool,
  isSelected: PropTypes.bool,
  isProgramDetail: PropTypes.bool,
  uniqueKey: PropTypes.string,
  feeSchedule: PropTypes.bool,
  offersSpecializedScreening: PropTypes.bool,
  showRequiresFeeScheduleScreening: PropTypes.bool,
  appRoles: PropTypes.arrayOf(PropTypes.string),
  forwardReferralParams: PropTypes.oneOfType([PropTypes.shape({
    serviceTypeName: PropTypes.string.isRequired,
    referralType: PropTypes.oneOf(['inbound', 'outbound']).isRequired,
    sensitive: PropTypes.bool.isRequired,
    referralId: PropTypes.string.isRequired
  }), PropTypes.oneOf([null])]),
  // FLAGS
  isSuperset: PropTypes.bool,
  uup459SupersetPhase2: PropTypes.bool,
  cerb1367ResourceListDetailEnhancements: PropTypes.bool,
  isEmr: PropTypes.bool,
  appRoles: PropTypes.array,
  cerbNy1115: PropTypes.bool
};
ProgramCard.defaultProps = {
  favoritesSectionRenderFn: () => {},
  selectedServicesIds: [],
  className: '',
  distance: '',
  deliveryLocation: [],
  hasHeaderDivider: true,
  headingLevel: 'h2',
  hours: {},
  isActive: false,
  isPrioritized: false,
  isSelected: false,
  isServingArea: false,
  isProgramDetail: false,
  onCardClick: () => {},
  onCardHover: () => {},
  onSelectProgram: undefined,
  phone: '',
  uniqueKey: '',
  feeSchedule: false,
  offersSpecializedScreening: false,
  showRequiresFeeScheduleScreening: false,
  status: null,
  appRoles: [],
  // FLAGS
  isSuperset: false,
  uup459SupersetPhase2: false,
  cerb1367ResourceListDetailEnhancements: false,
  isEmr: false,
  appRoles: [],
  cerbNy1115: false
};
export default /*#__PURE__*/React.memo(ProgramCard);
//# sourceMappingURL=ProgramCard.js.map